import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
  },
});

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Authorization': `Bearer ${session?.access_token || supabaseAnonKey}`,
    'Content-Type': 'application/json',
    'apikey': supabaseAnonKey,
  };
}

/**
 * Wraps any async operation. On 401/403, attempts a session refresh
 * and retries once. If refresh fails, triggers a full sign-out.
 */
export async function withAuthRetry<T>(
  operation: () => Promise<{ data: T; error: any }>,
): Promise<{ data: T; error: any }> {
  const result = await operation();

  const status = (result.error as any)?.status ?? (result.error as any)?.code;
  if (status === 401 || status === 403 || result.error?.message?.includes('JWT')) {
    const { error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      await supabase.auth.signOut();
      return result;
    }
    return operation();
  }

  return result;
}
